import { Tag } from '../Tag/Tag';
import './InputTags.scss';

interface TagDataInit {
    value: string
    hexColor?: string
}

export interface InputTagsConfig {
    MaxTags?: number
    MaxTagLength?: number
    Separators?: string[]
    Callbacks?: {
        OnTagAdd?: (event: CustomEvent<InputTags.AddEventData>) => void
        TagClick?: (e: CustomEvent<InputTags.ClickEventData>) => void
        Invalid?: (e: CustomEvent<InputTags.InvalidTagEventData>) => void
        StartTagEditting?: (e: CustomEvent<InputTags.StartTagEdittingEventData>) => void
        CancelTagEditting?: (e: CustomEvent<InputTags.CancelTagEdittingEventData>) => void
        FocusAdderText?: (e: CustomEvent<InputTags.FocusAdderTextEventData>) => void
        BlurAdderText?: (e: CustomEvent<InputTags.BlurAdderTextEventData>) => void
    }
}

type HTLMTagElement = HTMLDivElement & {
    __control: Tag
    // __tagData?: TagData
}
type HTLMAdderElement = HTMLDivElement & {
    __blurListener?: () => void
}

export class InputTags {
    private config: InputTagsConfig;
    private inputTagsContainer: HTMLDivElement;
    private adderElement: HTLMAdderElement;

    constructor(input: HTMLInputElement | undefined, config?: InputTagsConfig) {

        this.inputTagsContainer = document.createElement('div');
        this.adderElement = document.createElement('div');

        this.config = config || {};

        this.init(input);
    }

    public get _ControlContainer(): HTMLDivElement {
        return this.inputTagsContainer;
    }

    public _SetTags(tags: TagDataInit[]) {
        this.crearAll();
        this.addTags(tags);
    }

    public _GetTagByValue(value: string): Tag | undefined {
        return this.getTagElementByValue(value)?.__control
    }

    public _GetAllTags(): Tag[] {
        return this.getAllTagsElements().map(el => el.__control).filter(c => !!c)
    }

    public get _IsEdittingTag(): boolean {
        return this.getAllTagsElements().some(el => {
            return el.__control && el.__control._IsEdditting
        })
    }

    // ******************************************************************
    // Private methods
    // ******************************************************************

    private init(input: HTMLInputElement | undefined) {
        const extraClass = input?.className
        this.inputTagsContainer.className = `input-tags-container ${extraClass || ''}`;
        if (input?.parentElement) {
            input.parentElement.append(this.inputTagsContainer)
            input.remove()
        }

        this.adderElement.contentEditable = 'true';
        this.adderElement.className = 'adder';
        this.inputTagsContainer.appendChild(this.adderElement);

        this.inputTagsContainer.addEventListener('click', (event) => {
            if (event.target === this.inputTagsContainer) {
                this.adderElement.focus();
            }
        });
        if (this.config.Callbacks?.FocusAdderText) {
            this.adderElement.addEventListener("focus", () => {
                if (this.config.Callbacks?.FocusAdderText) {
                    const event = new CustomEvent<InputTags.FocusAdderTextEventData>('input-tags-adder-focus', {
                        detail: {}
                    });
                    this.config.Callbacks.FocusAdderText(event)
                }
            })
        }
        this.adderElement.addEventListener('keyup', (event) => {
            const textContent: string = this.adderElement?.textContent?.trim() || ""
            // console.warn('keyup adder', event.key, `"${textContent}"`);

            if (event.key === 'Backspace' && textContent === '') {
                this.removeLastTag();
            }
            else if (textContent !== '' && (event.key === 'Enter' || this.evalInputValueIncludeSeparators(event.key))) {
                const tagData: TagDataInit = { value: textContent };

                if (this.adderElement.__blurListener) this.adderElement.removeEventListener("blur", this.adderElement.__blurListener)
                this.evalInputValueAndAddTags(tagData)
                this.adderElement.focus();
                if (this.adderElement.__blurListener) this.adderElement.addEventListener("blur", this.adderElement.__blurListener)
            }
        });
        this.adderElement.__blurListener = () => {
            const textContent: string = this.adderElement?.textContent?.trim() || ""
            // console.warn('blur adder', `"${textContent}"`);

            if (textContent !== '') {
                const tagData: TagDataInit = { value: textContent };

                this.evalInputValueAndAddTags(tagData)
            }
            if (this.config.Callbacks?.BlurAdderText) {
                const event = new CustomEvent<InputTags.BlurAdderTextEventData>('input-tags-adder-blur', {
                    detail: {}
                });
                this.config.Callbacks?.BlurAdderText(event)
            }
        }
        this.adderElement.addEventListener("blur", this.adderElement.__blurListener)
    }

    private getTagElementByValue(value: string): HTLMTagElement | undefined {
        return this.getAllTagsElements().find(el => el.__control?._Value == value)
    }

    private getAllTagsElements(): HTLMTagElement[] {
        return Array.from(this.inputTagsContainer.children).filter((el: Element) => {
            return (el as HTLMTagElement).__control !== undefined;
        }) as HTLMTagElement[];
    }

    private crearAll() {
        const tagElements = this.getAllTagsElements();

        this.removeTagsByElement(tagElements);
    }

    private removeTagsByValues(tagsValues: string[]) {
        if (tagsValues.length === 0) return;
        tagsValues.forEach(tagValue => {
            const tagElements = Array.from(this.inputTagsContainer.children).filter((el: Element) => {
                const ctrl = (el as HTLMTagElement).__control
                return ctrl && ctrl._Value == tagValue;
            }) as HTLMTagElement[];

            this.removeTagsByElement(tagElements);
        });
    }

    private removeLastTag() {
        const children: HTLMTagElement[] = Array.from(this.inputTagsContainer.children) as HTLMTagElement[];
        let lastTag: HTLMTagElement | undefined;
        if (children.length > 1) {
            lastTag = children[children.length - 2]; // El último es adderElement, el penúltimo es el último tag
        }
        if (lastTag)
            this.removeTagsByElement([lastTag]);
    }

    private removeTagsByElement(tagElements: HTLMTagElement[]) {
        if (tagElements.length === 0) return;
        tagElements.forEach(tagElement => {
            if (tagElement.__control) {
                tagElement.__control._Destroy();
            }
        });
    }

    private addTags(tags: TagDataInit[]) {
        if (tags.length === 0) return;
        tags.forEach(tagData => {
            //if (this.evalTagValue(tagData)) {
            this.addTag(tagData, false);
            //}
        });
    }

    private addTag(tagData: TagDataInit, userAdd: boolean) {
        const tagControl = new Tag({
            Value: tagData.value,
            HexColor: tagData.hexColor,
            MaxTagLength: this.config.MaxTagLength,
            IsEditable: true,
            ShowCloserButton: true,
            ForceTrim: true,
            Callbacks: {
                OnKeyup: (e: KeyboardEvent) => {
                    // console.warn('keyup tag', e.key);
                    if (this.evalInputValueIncludeSeparators(e.key)) {
                        e.preventDefault();
                        tagControl._CancelEditting()

                        tagData.value = tagControl._Value
                        this.evalInputValueAndAddTags(tagData, tagElement);
                        this.adderElement.focus();
                    }
                },
                TagClick: (e: CustomEvent<Tag.ClickEventData>) => {
                    if (this.config.Callbacks?.TagClick) {
                        const event = new CustomEvent<InputTags.ClickEventData>('input-tag-click', {
                            detail: {
                                value: tagControl._Value,
                                hexColor: tagControl._HexColor,
                                tag: tagControl,
                                tagElement
                            }
                        });
                        this.config.Callbacks.TagClick(event);
                    }
                },
                StartEditting: (tagElement, initValue) => {
                    if (this.config.Callbacks?.StartTagEditting) {
                        const event = new CustomEvent<InputTags.StartTagEdittingEventData>('input-tag-start-editting', {
                            detail: {
                                initValue,
                                tag: tagControl,
                                tagElement
                            }
                        });
                        this.config.Callbacks?.StartTagEditting(event)
                    }
                },
                CancelEditting: (tagElement, endValue) => {
                    if (this.config.Callbacks?.CancelTagEditting) {
                        const event = new CustomEvent<InputTags.CancelTagEdittingEventData>('input-tag-cancel-editting', {
                            detail: {
                                endValue,
                                tag: tagControl,
                                tagElement
                            }
                        });
                        this.config.Callbacks?.CancelTagEditting(event)
                    }
                },
            }
        });

        const tagElement = tagControl._ControlContainer as HTLMTagElement;
        tagElement.__control = tagControl;

        this.inputTagsContainer.appendChild(tagElement);

        if (this.config.Callbacks?.OnTagAdd) {
            const event = new CustomEvent<InputTags.AddEventData>('input-tag-add', {
                detail: {
                    value: tagControl._Value,
                    hexColor: tagControl._HexColor,
                    tag: tagControl,
                    tagElement
                }
            });
            this.config.Callbacks?.OnTagAdd(event)
        }

        if (this.inputTagsContainer.lastChild !== this.adderElement) {
            this.inputTagsContainer.appendChild(this.adderElement);
        }
    }

    // ******************************************************************
    // Evaluaciones
    // ******************************************************************

    private evalTagValue(tagData: TagDataInit, isToAdd?: boolean): { valid: boolean, errorKey?: InputTags.ErrorKey } {
        const tagValue: string = tagData.value;
        let errorKey: InputTags.ErrorKey | undefined = undefined;
        let valid = true;

        let tagElements: HTLMTagElement[] | undefined = undefined

        if (this.config.MaxTagLength && tagValue.length > this.config.MaxTagLength) {
            errorKey = InputTags.INVALID_MAXTAGLENGTH;
        }
        else {
            tagElements = this.getAllTagsElements();
            const nCurrentTags = tagElements.length + (isToAdd ? 1 : 0);
            if (this.config.MaxTags && nCurrentTags > this.config.MaxTags) {
                errorKey = InputTags.INVALID_MAXTAGS;
            }
            else if (this.evalTagValueExists(tagValue, tagElements)) {
                errorKey = InputTags.INVALID_TAGALREADYEXISTS;
            }
        }
        if (errorKey) {
            if (this.config.Callbacks?.Invalid) {
                const event = new CustomEvent<InputTags.InvalidTagEventData>('input-tag-invalid', {
                    detail: {
                        value: tagValue,
                        errorKey
                    }
                });
                this.config.Callbacks.Invalid(event);
            }
            valid = false;
        }
        // console.warn(valid, tagValue, tagElements?.map(d => d.textContent), errorKey)
        return { valid, errorKey };
    }

    private evalInputValueIncludeSeparators(tagValue: string): boolean {
        return (!!this.config.Separators?.length && !!this.config.Separators.some(sep => tagValue.includes(sep)))
    }

    private evalTagValueExists(tagValue: string, tagElements = this.getAllTagsElements()): boolean {
        return tagElements.some(tagElement => {
            const tagControl = tagElement.__control;
            return tagControl && tagControl._Value === tagValue;
        });
    }

    private evalInputValueAndAddTags(tagData: TagDataInit, tagElementEditted?: HTLMTagElement) {
        const tagValue: string = tagData.value
        const tagsData: TagDataInit[] = []
        if (this.evalInputValueIncludeSeparators(tagValue)) {
            const separators = this.config.Separators || [];
            const values = tagValue.split(new RegExp(separators.join('|'), 'g'));
            values.forEach(value => {
                const trimmedValue = value.trim();
                if (trimmedValue) {
                    tagsData.push({ ...tagData, value: trimmedValue });
                }
            });
        } else {
            tagsData.push(tagData);
        }

        let firstTag: TagDataInit | undefined
        let lastTag: TagDataInit | undefined
        if (tagElementEditted && tagsData.length) {
            firstTag = tagsData.shift();
            lastTag = tagsData.pop();
        }
        for (const tagDataFin of tagsData) {
            const eva = this.evalTagValue(tagDataFin, !tagElementEditted)

            if (!eva.valid) {
                if (eva.errorKey == InputTags.INVALID_MAXTAGS) {
                    this.adderElement.textContent = '';
                }
                return;
            }

            this.addTag(tagDataFin, true);
        }

        if (tagElementEditted) {
            if (firstTag)
                tagElementEditted.__control._Value = firstTag.value
        }
        this.adderElement.textContent = lastTag ? lastTag.value : '';
    }
}

export namespace InputTags {
    export interface AddEventData {
        value: string,
        hexColor?: string,
        tag: Tag,
        tagElement: HTMLDivElement
    }
    export interface ClickEventData {
        value: string,
        hexColor?: string,
        tag: Tag,
        tagElement: HTMLDivElement
    }
    export interface InvalidTagEventData {
        value: string,
        errorKey: ErrorKey
    }
    export interface StartTagEdittingEventData {
        initValue: string,
        tag: Tag,
        tagElement: HTMLDivElement
    }
    export interface CancelTagEdittingEventData {
        endValue: string,
        tag: Tag,
        tagElement: HTMLDivElement
    }
    export interface FocusAdderTextEventData {
    }
    export interface BlurAdderTextEventData {
    }
    export const INVALID_MAXTAGLENGTH = 1;
    export const INVALID_MAXTAGS = 2;
    export const INVALID_TAGALREADYEXISTS = 3;
    export type ErrorKey = typeof INVALID_MAXTAGLENGTH | typeof INVALID_MAXTAGS | typeof INVALID_TAGALREADYEXISTS;
}